import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Copy, Check, AlertTriangle } from 'lucide-react';

interface CreateApiKeyDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess: () => void;
}

export function CreateApiKeyDialog({
    open,
    onOpenChange,
    onSuccess,
}: CreateApiKeyDialogProps) {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [permissions, setPermissions] = useState<string[]>([]);
    const [availablePermissions, setAvailablePermissions] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [createdKey, setCreatedKey] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (open) {
            fetchPermissions();
            resetForm();
        }
    }, [open]);

    const fetchPermissions = async () => {
        try {
            const token = localStorage.getItem('token');
            const apiUrl = import.meta.env.VITE_API_URL || '';

            const response = await fetch(`${apiUrl}/api/roles/permissions`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                setAvailablePermissions(data.permissions || []);
            }
        } catch (error) {
            console.error('Failed to fetch permissions:', error);
        }
    };

    const resetForm = () => {
        setName('');
        setPermissions([]);
        setCreatedKey(null);
        setError(null);
        setCopied(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            const apiUrl = import.meta.env.VITE_API_URL || '';

            const response = await fetch(`${apiUrl}/api/apiKeys`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    name,
                    permissions,
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setCreatedKey(data.data.key);
                onSuccess();
            } else {
                setError(data.error || t('apiKeys.dialogs.failedToCreate'));
            }
        } catch (error) {
            setError(t('apiKeys.dialogs.unexpectedError'));
        } finally {
            setIsLoading(false);
        }
    };

    const togglePermission = (permission: string) => {
        setPermissions((prev) =>
            prev.includes(permission)
                ? prev.filter((p) => p !== permission)
                : [...prev, permission]
        );
    };

    const handleCopy = () => {
        if (createdKey) {
            navigator.clipboard.writeText(createdKey);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleClose = () => {
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>
                        {createdKey ? t('apiKeys.dialogs.apiKeyCreated') : t('apiKeys.dialogs.createNewApiKey')}
                    </DialogTitle>
                    <DialogDescription>
                        {createdKey
                            ? t('apiKeys.dialogs.copyKeyWarning')
                            : t('apiKeys.dialogs.generateDescription')}
                    </DialogDescription>
                </DialogHeader>

                {createdKey ? (
                    <div className="space-y-4 py-4">
                        <div className="rounded-md border border-destructive/50 p-4 text-destructive dark:border-destructive">
                            <div className="flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4" />
                                <h5 className="font-medium leading-none tracking-tight">{t('apiKeys.dialogs.warning')}</h5>
                            </div>
                            <div className="mt-2 text-sm opacity-90">
                                {t('apiKeys.dialogs.copyNowWarning')}
                            </div>
                        </div>

                        <div className="flex items-center space-x-2">
                            <div className="grid flex-1 gap-2">
                                <Label htmlFor="apiKey" className="sr-only">
                                    {t('apiKeys.dialogs.apiKey')}
                                </Label>
                                <Input
                                    id="apiKey"
                                    defaultValue={createdKey}
                                    readOnly
                                    className="font-mono text-sm"
                                />
                            </div>
                            <Button size="sm" className="px-3" onClick={handleCopy}>
                                {copied ? (
                                    <Check className="h-4 w-4" />
                                ) : (
                                    <Copy className="h-4 w-4" />
                                )}
                                <span className="sr-only">{t('apiKeys.dialogs.copy')}</span>
                            </Button>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">{t('apiKeys.dialogs.name')}</Label>
                            <Input
                                id="name"
                                placeholder={t('apiKeys.dialogs.namePlaceholder')}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>{t('apiKeys.dialogs.permissions')}</Label>
                            <div className="h-[200px] w-full rounded-md border p-4 overflow-y-auto">
                                <div className="space-y-2">
                                    {availablePermissions.length > 0 ? (
                                        availablePermissions.map((permission) => (
                                            <div key={permission} className="flex items-center space-x-2">
                                                <Checkbox
                                                    id={permission}
                                                    checked={permissions.includes(permission)}
                                                    onCheckedChange={() => togglePermission(permission)}
                                                />
                                                <Label
                                                    htmlFor={permission}
                                                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                                >
                                                    {permission}
                                                </Label>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-sm text-muted-foreground text-center py-4">
                                            {t('apiKeys.dialogs.noPermissions')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div className="text-sm text-red-500 font-medium">{error}</div>
                        )}
                    </form>
                )}

                <DialogFooter>
                    {createdKey ? (
                        <Button onClick={handleClose}>{t('apiKeys.dialogs.done')}</Button>
                    ) : (
                        <>
                            <Button variant="outline" onClick={handleClose} type="button">
                                {t('common.cancel')}
                            </Button>
                            <Button type="submit" onClick={handleSubmit} disabled={isLoading}>
                                {isLoading ? t('common.loading') : t('apiKeys.dialogs.createKey')}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
